import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import moment from "moment";
import { useImmer } from "use-immer";
import { Icon } from "@iconify/react/dist/iconify.js";
import CloseOutlined from "@mui/icons-material/CloseOutlined";

import {
  Dialog,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
} from "@mui/material";

import { DataGrid } from "@mui/x-data-grid";
import { DateTimePicker } from "@mui/x-date-pickers";
import "./BroadcastDetail.css";
import Preview from "../WhatsappTemplate/Components/Preview";
import WarningDialog from "../../Components/WarningDialog";
import BroadcastContact from "../../Contact/BroadcastContact";
import mapWATemplate from "../../Helper/mapWATemplate";

import {
  createBroadcastDetail,
  deleteBroadcastDetail,
  getBroadcastDetailList,
  getWATemplateList,
  updateBroadcast,
} from "../../Store/Actions/WABAAction";

import { SET_BROADCAST_DETAIL_TABLE_PAGE } from "../../Store/Types/AgentTypes";

const BroadcastDetail = ({ selectedBroadcast }) => {
  const initialBroadcastData = {
    broadcastid: selectedBroadcast.BroadcastID,
    broadcastname: selectedBroadcast.BroadcastName,
    broadcasttime: selectedBroadcast.BroadcastTime,
  };

  const [broadcastData, setBroadcastData] = useState(initialBroadcastData);
  const [broadcastDetail, setBroadcastDetail] = useState([]);
  const [count, setCount] = useState(0);
  const [isAddingInProgress, setIsAddingInProgress] = useState(false);
  const [isAddingRecipients, setIsAddingRecipients] = useState(false);

  const [isDeleteRecipientWarningShown, setIsDeleteRecipientWarningShown] =
    useState(false);

  const [isEditingData, setIsEditingData] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isPreviewShown, setIsPreviewShown] = useState(false);
  const [isRemovingInProgress, setIsRemovingInProgress] = useState(false);
  const [isRemovingRecipients, setIsRemovingRecipients] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [recipientsToRemove, setRecipientsToRemove] = useState([]);
  const [skip, setSkip] = useState(0);
  const dispatch = useDispatch();
  const { broadcastDetailTablePage } = useSelector((state) => state.agent);

  const initialComponentsValues = {
    header: {
      mediaType: "none",

      attachment: {
        name: null,
        type: null,
        size: null,
        file: null,
      },

      headerText: null,
      headerVariable: null,
    },

    bodyText: "Sample Body Text",
    bodyVariable1: null,
    bodyVariable2: null,
    bodyVariable3: null,
    bodyVariable4: null,
    bodyVariable5: null,
    bodyVariable6: null,
    bodyVariable7: null,
    bodyVariable8: null,
    bodyVariable9: null,
    bodyVariable10: null,
    footerText: "",
    button: [],
    isCallPermission: false,
  };

  // useImmer used for easier state update
  const [componentsValues, updateComponentsValues] = useImmer(
    initialComponentsValues
  );

  const columns = [
    {
      field: "ContactName",
      flex: 1,
      headerName: "Recipient Name",
    },

    {
      field: "RecipientWAID",
      flex: 1,
      headerName: "Phone Number",
    },

    {
      field: "SendTime",
      flex: 1,
      headerName: "Send Time",

      renderCell: (params) =>
        params.row.SendTime
          ? moment.utc(params.row.SendTime).format("DD-MMM-yyyy HH:mm:ss")
          : "",
    },

    {
      field: "Status",
      flex: 1,
      headerName: "Status",

      renderCell: (params) => {
        let color;

        switch (params.row.Status) {
          case "sent":
          case "read":
          case "delivered":
            color = "#07BC0C";
            break;

          case "failed":
            color = "#FF0000";
            break;

          default:
            color = "#0343C7";
            break;
        }

        return (
          <p style={{ color: color, textTransform: "capitalize" }}>
            {params.row.Status ?? "scheduled"}
          </p>
        );
      },
    },
  ];

  useEffect(() => {
    fetchBroadcastDetail();

    // Get the template body
    getWATemplateList()
      .then((response) => {
        if (response.status) {
          updateComponentsValues((draft) =>
            mapWATemplate(
              draft,
              response.data.filter(
                (template) => template.ID === selectedBroadcast.TemplateID
              )[0]
            )
          );

          const components = JSON.parse(selectedBroadcast.Components).template
            .components;

          console.log(components);

          updateComponentsValues((draft) => {
            draft.header.headerVariable = components[0].parameters[0]?.text;
          });

          components[1].parameters.forEach((element, index) => {
            updateComponentsValues((draft) => {
              draft[`bodyVariable${index + 1}`] = element.text;
            });
          });
        } else {
          console.log("error", response.error);
        }
      })
      .catch((err) => {
        console.log("err", err);
      });
  }, [selectedBroadcast]);

  function addRecipient(contactsToAdd) {
    setIsAddingInProgress(true);

    createBroadcastDetail({
      broadcastdetail: contactsToAdd.map((contact) => {
        return {
          broadcastid: selectedBroadcast.BroadcastID,
          contactid: contact.split("-")[0],
          recipientwaid: contact.split("-")[1],
        };
      }),
    })
      .then((response) => {
        if (response.status) {
          toast.info(
            <>
              <p>{contactsToAdd.length} contacts added successfully.</p>
            </>,

            {
              autoClose: 5000,
            }
          );

          setIsAddingInProgress(false);
          setIsAddingRecipients(false);
          fetchBroadcastDetail(skip);
        } else {
          console.log("error adding recipient", response.error);
          setIsAddingInProgress(false);

          toast.error("error adding recipient", {
            autoClose: 5000,
          });
        }
      })
      .catch((err) => {
        console.log("error adding recipient", err);
        setIsAddingInProgress(false);

        toast.error("error adding recipient", {
          autoClose: 5000,
        });
      });
  }

  function deleteRecipient() {
    setIsRemovingInProgress(true);

    deleteBroadcastDetail({
      broadcastdetail: recipientsToRemove.map((recipient) => ({
        broadcastdetailid: recipient,
      })),
    })
      .then((response) => {
        if (response.status) {
          toast.info(
            <>
              <p>Broadcast updated successfully.</p>
            </>,

            {
              autoClose: 5000,
            }
          );

          fetchBroadcastDetail(skip);
          setIsRemovingRecipients(false);
          setIsRemovingInProgress(false);
          setIsDeleteRecipientWarningShown(false);
        } else {
          console.log("error deleting recipient", response.error);
          setIsRemovingInProgress(false);

          toast.error("error deleting recipient", {
            autoClose: 5000,
          });
        }
      })
      .catch((err) => {
        console.log("error deleting recipient", err);
        setIsRemovingInProgress(false);

        toast.error("error deleting recipient", {
          autoClose: 5000,
        });
      });
  }

  function fetchBroadcastDetail(skip) {
    setIsLoading(true);

    getBroadcastDetailList(selectedBroadcast.BroadcastID, skip)
      .then((response) => {
        if (response.status) {
          process.env.NODE_ENV === "development" &&
            console.log(
              "broadcast detail: ",
              response.data.broadcastdetaildata
            );

          setBroadcastDetail(response.data.broadcastdetaildata ? response.data.broadcastdetaildata : []);
          setCount(response.data.countdata);
          setIsLoading(false);
        } else {
          console.log("error fetching broadcast detail", response.error);
          setIsLoading(false);

          toast.error("error fetching broadcast detail", {
            autoClose: 5000,
          });
        }
      })
      .catch((err) => {
        console.log("error fetching broadcast detail", err);
        setIsLoading(false);

        toast.error("error fetching broadcast detail", {
          autoClose: 5000,
        });
      });
  }

  return (
    <section className="broadcast-detail">
      <div className="data">
        <header>
          <p>Broadcast Detail from table {selectedBroadcast.TableName}</p>

          {selectedBroadcast.Status === "NEW" && (
            <Tooltip
              arrow
              placement="left"
              title={isEditingData ? "Close" : "Edit"}
            >
              <IconButton
                size="small"
                onClick={() => setIsEditingData(!isEditingData)}
              >
                <Icon
                  color="#0343C7"
                  height="26"
                  icon={isEditingData ? "ph:x-bold" : "material-symbols:edit"}
                />
              </IconButton>
            </Tooltip>
          )}
        </header>

        <form method="post">
          <div className="form-container">
            <TextField
              InputProps={{ readOnly: !isEditingData }}
              label="Broadcast Name"
              value={broadcastData.broadcastname}
              onChange={(e) =>
                setBroadcastData({
                  ...broadcastData,
                  broadcastname: e.target.value,
                })
              }
            />

            <TextField
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip arrow placement="top" title="Show template">
                      <Icon
                        height="22"
                        icon="ic:baseline-remove-red-eye"
                        style={{ cursor: "pointer" }}
                        onClick={() => setIsPreviewShown(true)}
                      />
                    </Tooltip>
                  </InputAdornment>
                ),

                readOnly: true,
              }}
              label="Template"
              value={selectedBroadcast.TemplateName}
            />

            <TextField
              InputProps={{ readOnly: true }}
              label="Whatsapp Number"
              value={`${selectedBroadcast.WABAAccount} (${selectedBroadcast.WAID})`}
            />

            <TextField
              className={`status-field ${
                selectedBroadcast.Status === "NEW"
                  ? "new"
                  : selectedBroadcast.Status === "ONGOING"
                  ? "ongoing"
                  : "ended"
              }`}
              InputProps={{ readOnly: true }}
              label="Status"
              value={selectedBroadcast.Status}
            />

            <DateTimePicker
              ampm={false}
              label="Broadcast Time"
              minDateTime={selectedBroadcast.Status === "NEW" && moment()}
              readOnly={!isEditingData}
              renderInput={(params) => <TextField {...params} />}
              value={moment.utc(broadcastData.broadcasttime)}
              onChange={(value) =>
                setBroadcastData({
                  ...broadcastData,
                  broadcasttime: value.toISOString(true),
                })
              }
            />

            <TextField
              InputProps={{ readOnly: true }}
              label="Language"
              value="Indonesia"
            />
          </div>

          {isEditingData && (
            <section className="buttons">
              <button
                className="cancel"
                type="button"
                onClick={() => {
                  setBroadcastData(initialBroadcastData);
                  setIsEditingData(false);
                }}
              >
                Cancel
              </button>

              <button
                className="confirm"
                disabled={isUpdating}
                type="button"
                onClick={() => {
                  setIsUpdating(true);

                  updateBroadcast(broadcastData)
                    .then((response) => {
                      if (response.status) {
                        toast.info(
                          <>
                            <p>Broadcast updated successfully.</p>
                          </>,

                          {
                            autoClose: 5000,
                          }
                        );

                        setIsEditingData(false);
                        setIsUpdating(false);
                      } else {
                        console.log("error updating broadcast", response.error);
                        setIsUpdating(false);

                        toast.error("error updating broadcast", {
                          autoClose: 5000,
                        });
                      }
                    })
                    .catch((err) => {
                      console.log("error updating broadcast", err);
                      setIsUpdating(false);

                      toast.error("error updating broadcast", {
                        autoClose: 5000,
                      });
                    });
                }}
              >
                Confirm
              </button>
            </section>
          )}
        </form>
      </div>

      <div className="recipient-list">
        <header>
          <p>Recipient List</p>

          {selectedBroadcast.Status === "NEW" &&
            (isAddingRecipients || isRemovingRecipients ? (
              <Tooltip arrow placement="bottom" title="Close">
                <IconButton
                  size="small"
                  onClick={() => {
                    setIsAddingRecipients(false);
                    setIsRemovingRecipients(false);
                  }}
                >
                  <Icon color="#0343C7" height="26" icon="ph:x-bold" />
                </IconButton>
              </Tooltip>
            ) : (
              <div>
                <Tooltip arrow placement="bottom" title="Remove recipients">
                  <IconButton
                    size="small"
                    onClick={() => setIsRemovingRecipients(true)}
                  >
                    <Icon
                      color="#0343C7"
                      height="26"
                      icon="material-symbols:remove-rounded"
                    />
                  </IconButton>
                </Tooltip>

                <Tooltip arrow placement="bottom" title="Add recipients">
                  <IconButton
                    size="small"
                    onClick={() => setIsAddingRecipients(true)}
                  >
                    <Icon
                      color="#0343C7"
                      height="26"
                      icon="material-symbols:add-rounded"
                    />
                  </IconButton>
                </Tooltip>
              </div>
            ))}
        </header>

        <DataGrid
          autoPageSize
          checkboxSelection={isRemovingRecipients}
          className="table"
          columns={columns}
          disableColumnFilter
          disableColumnMenu
          disableSelectionOnClick
          keepNonExistentRowsSelected
          loading={isLoading}
          page={broadcastDetailTablePage}
          pageSize={10}
          paginationMode={"server"}
          rowCount={count}
          rows={broadcastDetail}
          rowsPerPageOptions={[]} // hide rows per page
          selectionModel={recipientsToRemove}
          getRowId={(row) => row.ID}
          onPageChange={(newPage) => {
            if (newPage > broadcastDetailTablePage) {
              fetchBroadcastDetail(skip + 10);
              setSkip((prevState) => prevState + 10);
            } else {
              fetchBroadcastDetail(skip - 10);
              setSkip((prevState) => prevState - 10);
            }

            dispatch({
              type: SET_BROADCAST_DETAIL_TABLE_PAGE,
              payload: newPage,
            });
          }}
          onSelectionModelChange={(selected) => setRecipientsToRemove(selected)}
        />

        {isRemovingRecipients && (
          <section className="buttons">
            <button
              className="cancel"
              type="button"
              onClick={() => {
                setRecipientsToRemove([]);
                setIsRemovingRecipients(false);
              }}
            >
              Cancel
            </button>

            <button
              className="confirm"
              disabled={isRemovingInProgress}
              type="button"
              onClick={() => setIsDeleteRecipientWarningShown(true)}
            >
              Confirm
            </button>
          </section>
        )}
      </div>

      <Dialog
        open={isPreviewShown}
        className="dialog preview-dialog"
        fullWidth
        maxWidth="sm"
        scroll="paper"
        onClose={() => setIsPreviewShown(false)}
      >
        <div className="dialog-container">
          <div className="dialog-content">
            <IconButton
              className="close-button"
              onClick={() => setIsPreviewShown(false)}
            >
              <CloseOutlined />
            </IconButton>

            <div className="title">
              <p>{selectedBroadcast.TemplateName}</p>
            </div>

            <Preview
              componentsValues={componentsValues}
              isCallPermission={componentsValues.isCallPermission}
            />
          </div>
        </div>
      </Dialog>

      <BroadcastContact
        addRecipient={addRecipient}
        currentTableID={selectedBroadcast.TableID}
        currentTableName={selectedBroadcast.TableName}
        isAddingInProgress={isAddingInProgress}
        isAddingRecipients
        isOpen={isAddingRecipients}
        setIsBroadcastContactShown={setIsAddingRecipients}
      />

      <WarningDialog
        confirmAction={deleteRecipient}
        contents={
          <>
            <p>
              You are about to remove{" "}
              <span>{recipientsToRemove.length} recipients</span> from this
              broadcast.
            </p>
            <p>Are you sure?</p>
          </>
        }
        isOpen={isDeleteRecipientWarningShown}
        isProcessing={isRemovingInProgress}
        setIsOpen={setIsDeleteRecipientWarningShown}
      />
    </section>
  );
};

export default BroadcastDetail;
